/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import thunk from 'redux-thunk';
import createDebounce from 'redux-debounced';
import { Reducer, Store, Middleware } from 'redux';
import i18next from 'i18next';

import { HttpClient } from '@wix/http-client';

import configureStore from '../store/configureStore';
import uploadMiddleware from '../middleware/upload-middleware';
import { biMiddlewareUoU } from '../middleware/bi-events-middleware';
import { setConfig } from './config';
import { setupI18n } from './i18n';
import { resizeOnHeightChange } from './auto-resizing';
import createRequest from './create-request';
import uploadFile from './upload-file';
import generateClasses from './wix-class-generator';
import requestCredentials from './request-credentials';
import Wix from './wix-sdk-polyfill';
import listenToViewedUserChange from './listen-to-viewed-user-change';
import listenToEditModeChange from './listen-to-edit-mode-change';
import listenToNavigationChange from './listen-to-navigation-change';
import listenToSettingsChange from './listen-to-settings-change';
import listenToStyleParamsChange from './listen-to-style-params-change';
import { listenToSignedInstanceChange } from './listen-to-signed-instance-change';
import listenToWixEvents from './wix-redux';
import { listenToSiteSaved } from './listen-to-site-status-events';
import { renderOnlyWithStyles } from './render-only-with-styles';
import { listenToFrames } from './frames-messages';
import { setSiteInfo } from '../actions/set-site-info';
import wixEvents from '../state/wix-events';
import { getInstance as getFedopsInstance } from './fedops';
import { isPopup, getViewMode, isDemoMode, getSignedInstance } from '../state/site/selectors';
import { VIEW_MODE_EDITOR, VIEW_MODE_PREVIEW } from '../constants/view-modes';
import { getBadgesBiLogger } from './bi-badges';
import { appLoaded, setComponentId, setOriginalComponentId } from '../state/site/actions';

const FALLBACK_LANGUAGE = 'en';

interface Context<State> {
  store: Store<State>;
  i18n: i18next.i18n;
  fedops: any;
}

interface ContextLoaderProps<State> {
  reducer: Reducer<State>;
  middlewaresToAdd?: Middleware[];
  shouldListenToUserChange?: boolean;
}

const requestFactory = (baseUrl: string, locale: string) => <State>(state: State) =>
  createRequest({
    baseUrl,
    instance: getSignedInstance(state),
    getInstance: () => getSignedInstance(state),
    locale,
  });

const withExtraArgument = (baseUrl: string, locale: string) => <State>(store: Store<State>) =>
  thunk.withExtraArgument({
    request: requestFactory(baseUrl, locale)(store.getState()),
    baseUrl,
    badgesBiLogger: getBadgesBiLogger(),
    httpClientInstance: new HttpClient({
      baseURL: window.location.origin,
      getAppToken: () => getSignedInstance(store.getState()),
    }),
  })(store);

// To Do: Test this
export const contextLoader = <State>({
  reducer,
  middlewaresToAdd = [],
  shouldListenToUserChange = true,
}: ContextLoaderProps<State>): Context<State> => {
  window.__CONFIG__ && setConfig(window.__CONFIG__);

  const i18n = setupI18n(window);
  const baseUrl = window.__BASE_PATH__ ?? '/';
  const locale = window.__LOCALE__ ?? FALLBACK_LANGUAGE;
  const initialState = _.omit(window.__INITIAL_STATE__ || {}, ['textEditor']);

  const middlewares = [
    createDebounce(),
    withExtraArgument(baseUrl, locale),
    uploadMiddleware(uploadFile, requestCredentials, requestFactory(baseUrl, locale)),
    biMiddlewareUoU,
    ...middlewaresToAdd,
  ];

  // TODO: https://app.asana.com/0/442188984678470/459711063526717
  const store = configureStore<State>(initialState, middlewares, reducer);
  const isAppPopup = isPopup(initialState);
  const state = store.getState();
  const viewMode = getViewMode(state);

  renderOnlyWithStyles(store);
  listenToStyleParamsChange(store, viewMode);

  if (!isAppPopup) {
    resizeOnHeightChange();
  }

  store.dispatch(setComponentId(Wix.Utils.getCompId()));

  const origCompId = Wix.Utils.getOrigCompId();
  if (origCompId) {
    store.dispatch(setOriginalComponentId(origCompId));
  }

  Wix.getSiteInfo((data) => {
    store.dispatch(setSiteInfo(data));
  });

  const isEditor = viewMode === VIEW_MODE_PREVIEW || viewMode === VIEW_MODE_EDITOR;
  if (isEditor && !isDemoMode(state)) {
    listenToEditModeChange(store);
    listenToSettingsChange(store);
    listenToSiteSaved(store);
  }

  listenToSignedInstanceChange(store);

  if (Wix && window.__CUSTOM_STYLES__) {
    Wix.addEventListener(Wix.Events.STYLE_PARAMS_CHANGE, generateClasses(store, window.__CUSTOM_STYLES__));
    delete window.__CUSTOM_STYLES__;
  }

  listenToNavigationChange(store);
  listenToWixEvents(store, wixEvents);
  shouldListenToUserChange && listenToViewedUserChange(store);
  listenToFrames(store);

  store.dispatch(appLoaded());

  delete window.__INITIAL_STATE__;
  delete window.__CONFIG__;
  delete window.__LOCALE__;
  delete window.__BASE_PATH__;

  return {
    store,
    i18n,
    fedops: getFedopsInstance(),
  };
};
